import React, { useState, useEffect, useRef } from 'react';
import { FormField, FileData, FileFieldValue } from '../../types/forms';
import { useApplicationStore } from '../../store/application.store';
import { FileUpload } from './FileUpload';
import { AlertCircle } from 'lucide-react';
import { fileService } from '../../services/file.service';
import { SignatureField } from './SignatureField';
import { useFormField } from '../../hooks/use-form-field.hook';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/features/ui/components/Select';
import { Input } from '@/features/ui/primitives/input';
import { Label } from '@/features/ui/primitives/label';
import { Textarea } from '@/features/ui/primitives/textarea';
import { Checkbox } from '@/features/ui/primitives/checkbox';
import { cn } from '@/lib/utils';

interface FieldProps {
  field: FormField;
  onFieldChange?: (value: string | boolean | FileFieldValue | null) => void;
  disabled?: boolean;
}

// Purpose: Get the maximum length for a field based on its type and configuration
const getFieldMaxLength = (field: FormField): number | undefined => {
  if (field.type === 'phone') return 14; // (xxx) xxx-xxxx
  if (field.type === 'tax-id' || field.id === 'federalTaxId') return 10; // xx-xxxxxxx
  if (field.type === 'ssn' || field.id.includes('SSN')) return 11; // xxx-xx-xxxx

  // Validate formFieldMaxLength - must be positive
  const formFieldMaxLength =
    field.formFieldMaxLength && field.formFieldMaxLength > 0 ? field.formFieldMaxLength : undefined;

  // Validate crmMaxLength - must be positive
  const crmMaxLength =
    field.crmMaxLength && field.crmMaxLength > 0 ? field.crmMaxLength : undefined;

  return formFieldMaxLength || crmMaxLength || undefined;
};

// Purpose: Renders a form field with proper canonical/presentation value separation
export const Field = ({ field, onFieldChange, disabled = false }: FieldProps) => {
  const { viewableFields, editableFields } = useApplicationStore();

  // Use the custom hook for all field state management
  const {
    displayValue,
    handleChange,
    handleSelectChange,
    handleCheckboxChange,
    handleFileChange,
    handleSignatureChange,
    handleBlur,
    isInvalid,
    errorMessage,
  } = useFormField({ field, onFieldChange, disabled });

  // State for custom picklist dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if current signer can view/edit this field
  const canView = viewableFields.includes(field.id);
  const canEdit = editableFields.includes(field.id);

  // Effect to handle clicks outside the custom dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup listener on component unmount or when dropdown closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!canView) {
    return null;
  }

  // Purpose: Handle file field changes with caching
  const handleFileFieldChange = (files: FileFieldValue) => {
    // For file fields, store in cache
    if (field.type === 'file' && Array.isArray(files)) {
      fileService.storeFiles(field.id, files as FileData[]);
    }
    handleFileChange(files);
  };

  // Render validation error message if field is invalid
  const renderValidationError = () => {
    if (!isInvalid || !errorMessage) return null;

    return (
      <div className="mt-1 text-xs text-red-600 flex !flex-row items-start justify-start md:absolute md:top-full md:left-0 md:mt-1 z-10">
        <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
        {errorMessage}
      </div>
    );
  };

  // Purpose: Render different field types based on field configuration
  switch (field.type) {
    case 'file':
      return (
        <div className="mb-3 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <FileUpload
            onChange={(files) => {
              // Always call handleFileFieldChange to reflect the current state (including empty)
              handleFileFieldChange(files); // Pass the array (empty or not)
            }}
            disabled={disabled || !canEdit}
            acceptedTypes={field.acceptedFileTypes?.join(',') || 'application/pdf'}
            existingFiles={
              Array.isArray(field.value)
                ? (field.value as FileData[]).filter((f) => f && f.name && f.contentType)
                : []
            }
            isInvalid={isInvalid}
          />
          {field.description && <p className="mt-1 text-sm text-gray-500">{field.description}</p>}
          {renderValidationError()}
        </div>
      );

    case 'text':
    case 'email':
    case 'tax-id':
    case 'ssn':
      return (
        <div className="mb-2 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={field.id}
            data-testid={`form-field-input-${field.id}`}
            type={field.type === 'email' ? 'email' : 'text'}
            className={cn(
              'text-xs lg:text-xs xl:text-xs h-8 md:h-9', // Reduced font and height sizes
              isInvalid && 'border-red-500 bg-red-50',
              !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
            )}
            value={displayValue}
            placeholder={field.placeholder !== undefined ? String(field.placeholder) : ''}
            onChange={handleChange}
            required={field.required}
            disabled={!canEdit}
            maxLength={getFieldMaxLength(field)}
            onBlur={handleBlur}
          />
          {renderValidationError()}
        </div>
      );

    case 'phone':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={field.id}
            data-testid={`form-field-input-${field.id}`}
            type={field.type === 'phone' ? 'tel' : field.type}
            className={cn(
              'text-xs lg:text-xs xl:text-xs h-8 md:h-9', // Reduced font and height sizes
              isInvalid && 'border-red-500 bg-red-50',
              !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
            )}
            value={displayValue}
            placeholder={field.placeholder !== undefined ? String(field.placeholder) : ''}
            onChange={handleChange}
            required={field.required}
            disabled={!canEdit}
            maxLength={getFieldMaxLength(field)}
            onBlur={handleBlur}
          />
          {renderValidationError()}
        </div>
      );

    case 'number':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={field.id}
            data-testid={`form-field-input-${field.id}`}
            type="text"
            inputMode="numeric"
            className={cn(
              'text-xs lg:text-xs xl:text-xs h-8 md:h-9', // Reduced font and height sizes
              isInvalid && 'border-red-500 bg-red-50',
              !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
            )}
            value={displayValue}
            placeholder={field.placeholder !== undefined ? String(field.placeholder) : ''}
            onChange={handleChange}
            required={field.required}
            disabled={!canEdit}
            maxLength={getFieldMaxLength(field)}
            onBlur={handleBlur}
          />
          {renderValidationError()}
        </div>
      );

    case 'textarea':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Textarea
            id={field.id}
            className={cn(
              'text-xs lg:text-xs xl:text-xs h-9 md:h-11', // Reduced font sizes, keep textarea height reasonable
              isInvalid && 'border-red-500 bg-red-50',
              !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
            )}
            value={displayValue}
            placeholder={field.placeholder !== undefined ? String(field.placeholder) : ''}
            onChange={handleChange}
            required={field.required}
            rows={4}
            disabled={!canEdit}
            maxLength={getFieldMaxLength(field)}
            onBlur={handleBlur}
          />
          {renderValidationError()}
        </div>
      );

    case 'date':
    case 'datetime':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={field.id}
            data-testid={`form-field-input-${field.id}`}
            type={field.type === 'datetime' ? 'datetime-local' : 'date'}
            className={cn(
              'text-xs lg:text-xs xl:text-xs h-8 md:h-9', // Reduced font and height sizes
              isInvalid && 'border-red-500 bg-red-50',
              !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
            )}
            value={displayValue}
            onChange={handleChange}
            required={field.required}
            disabled={!canEdit}
            onBlur={handleBlur}
          />
          {renderValidationError()}
        </div>
      );

    case 'checkbox':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <div className="flex items-center space-x-3">
            <Checkbox
              id={field.id}
              checked={Boolean(field.value)}
              onCheckedChange={handleCheckboxChange}
              disabled={!canEdit}
              aria-invalid={isInvalid}
            />
            <Label
              htmlFor={field.id}
              className={`text-xs font-medium ${!canEdit ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700'}`}
            >
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </Label>
          </div>
          {field.description && (
            <p className={`mt-1 text-xs ${!canEdit ? 'text-gray-400' : 'text-gray-500'}`}>
              {field.description}
            </p>
          )}
          {renderValidationError()}
        </div>
      );

    case 'terms':
      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <div className="flex items-start space-x-3">
            <Checkbox
              id={field.id}
              checked={Boolean(field.value)}
              onCheckedChange={handleCheckboxChange}
              disabled={!canEdit}
              aria-invalid={isInvalid}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor={field.id}
                className={`text-xs font-medium ${!canEdit ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700'}`}
              >
                {field.type === 'terms' ? field.termsText : field.label}
                {field.required && <span className="text-red-500">*</span>}
              </Label>
            </div>
          </div>
          {renderValidationError()}
        </div>
      );

    case 'signature':
      return (
        <div className="space-y-1 relative" id={`field-${field.id}`}>
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <SignatureField
            id={field.id}
            value={field.value as string}
            onChange={handleSignatureChange}
            disabled={!canEdit || disabled}
            isInvalid={isInvalid}
          />
          {renderValidationError()}
        </div>
      );

    case 'picklist': {
      const options =
        Array.isArray(field.crmPicklistValues) && field.crmPicklistValues.length > 0
          ? field.crmPicklistValues
              .filter((o) => !!o)
              .map((o) => ({
                label: String(o.label ?? o.value),
                value: String(o.value),
              }))
          : Array.isArray(field.currentPicklistOptions)
            ? field.currentPicklistOptions.map((o) => ({
                // For many CRMs (and especially Salesforce country/state), the stored value is the
                // *label* (e.g. "United States", "Florida") even if we also have an ISO/code.
                // Using the label as the Select's value keeps the UI + validation consistent.
                label: String(o.label),
                value: String(o.label),
              }))
            : [];

      return (
        <div className="mb-1 md:mb-2 relative" id={`field-${field.id}`}>
          <Label className="block text-xs font-medium text-gray-700 mb-1" htmlFor={field.id}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Select
            value={typeof field.value === 'string' ? field.value : undefined}
            onValueChange={handleSelectChange}
            disabled={disabled || !canEdit}
          >
            <SelectTrigger
              className={cn(
                'text-xs lg:text-xs xl:text-xs h-8 md:h-9', // Reduced font and height sizes
                isInvalid ? 'border-red-500 bg-red-50' : 'border-gray-300',
                !canEdit && 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
              )}
            >
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {Array.isArray(options) &&
                options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {renderValidationError()}
        </div>
      );
    }

    default:
      return null;
  }
};
